import type { NetworkInfo } from "@alch/dx-entities";
import { Card } from "@alch/ui";
import ComposerParams from "@features/composer/ComposerParams";
import useMakeComposerRequest from "@features/composer/useMakeComposerRequest";
import { useScopedApp } from "@hooks/useScopedApp.ts";
import { FormProvider } from "react-hook-form";
import ComposerMethodSelect from "./ComposerMethodSelect.js";
import ComposerRequest from "./ComposerRequest.js";
import type { ComposerMethodConfig } from "./lib/methods.js";
import { getUniqueMethodConfig } from "./lib/methods.js";
import useComposerForm from "./useComposerForm.js";

interface ComposerFormProps {
  methodConfigs: ComposerMethodConfig[];
  networkInfos: NetworkInfo[];
  baseUrl: string;
  isPublic?: boolean;
}

const ComposerForm = ({
  methodConfigs,
  baseUrl,
  networkInfos,
  isPublic,
}: ComposerFormProps) => {
  const scopedApp = useScopedApp({
    enabled: !isPublic,
  });
  const form = useComposerForm({
    methodConfigs,
    search: window.location.search,
    defaultOverrides: {
      app: scopedApp?.data?.authToken,
    },
  });
  const networkId = form.watch("network");
  const networkInfo = networkInfos.find((info) => info.id === networkId);
  const methodConfig = getUniqueMethodConfig(methodConfigs, {
    method: form.watch("method"),
    network: networkId,
  });

  const mutation = useMakeComposerRequest({ methodConfig, networkInfo });

  return (
    <FormProvider {...form}>
      <Card>
        <form
          className="flex grow gap-6 p-6 text-primary max-lg:flex-col"
          onSubmit={form.handleSubmit(
            (state) => mutation.mutate(state),
            console.warn,
          )}
        >
          <div className="flex min-w-0 shrink-0 flex-col gap-6 lg:w-1/3">
            <ComposerMethodSelect
              control={form.control}
              methodConfigs={methodConfigs}
              networkInfos={networkInfos}
              enableAppSelect={!isPublic && scopedApp?.data?.authToken == null}
            />

            {methodConfig && (
              <ComposerParams
                control={form.control}
                methodConfig={methodConfig}
              />
            )}
          </div>

          <ComposerRequest
            className="min-w-0 grow"
            control={form.control}
            methodConfig={methodConfig}
            networkInfo={networkInfo}
            mutation={mutation}
            baseUrl={baseUrl}
          />
        </form>
      </Card>
    </FormProvider>
  );
};

export default ComposerForm;
